const KEY_PREFIX = 'brij-gtm-registration-form-response-';
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export type StoredFormResponseAnswer = {
  question: string;
  answer: any;
  property: string;
};

export type StoredFormResponse = {
  slug: string;
  formModuleId: string;
  userUid: string | null;
  capturedAt: number;
  responseWithProperty: StoredFormResponseAnswer[];
};

const buildKey = (slug: string, formModuleId: string) =>
  `${KEY_PREFIX}${slug}-${formModuleId}`;

const safeSession = (): Storage | null => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage
      ? window.sessionStorage
      : null;
  } catch {
    return null;
  }
};

export const saveRegistrationFormResponse = (snapshot: StoredFormResponse) => {
  const storage = safeSession();
  if (!storage) return;
  if (!snapshot.slug || !snapshot.formModuleId) return;

  try {
    storage.setItem(
      buildKey(snapshot.slug, snapshot.formModuleId),
      JSON.stringify(snapshot)
    );
  } catch (err) {
    console.warn('[brij.registrationFormResponse] save failed:', err);
  }
};

export const readRegistrationFormResponse = (
  slug: string,
  formModuleId: string,
  expectedUserUid?: string | null
): StoredFormResponse | null => {
  const storage = safeSession();
  if (!storage || !slug || !formModuleId) return null;

  const key = buildKey(slug, formModuleId);

  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: StoredFormResponse | null = null;
  try {
    parsed = JSON.parse(raw) as StoredFormResponse;
  } catch {
    storage.removeItem(key);
    return null;
  }
  if (!parsed) return null;

  const slugMismatch = parsed.slug !== slug;
  const formMismatch = parsed.formModuleId !== formModuleId;
  const uidMismatch =
    typeof expectedUserUid !== 'undefined' &&
    expectedUserUid !== null &&
    parsed.userUid !== null &&
    parsed.userUid !== expectedUserUid;

  const isStale =
    typeof parsed.capturedAt !== 'number' ||
    Date.now() - parsed.capturedAt > STALE_AFTER_MS;

  if (slugMismatch || formMismatch || uidMismatch || isStale) {
    storage.removeItem(key);
    return null;
  }

  return parsed;
};

export const clearRegistrationFormResponse = (
  slug: string,
  formModuleId: string
) => {
  const storage = safeSession();
  if (!storage || !slug || !formModuleId) return;
  try {
    storage.removeItem(buildKey(slug, formModuleId));
  } catch {
    // noop
  }
};

export const clearAllRegistrationFormResponses = (slug?: string) => {
  const storage = safeSession();
  if (!storage) return;
  try {
    const matchPrefix = slug ? `${KEY_PREFIX}${slug}-` : KEY_PREFIX;
    const toDelete: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(matchPrefix)) toDelete.push(key);
    }
    toDelete.forEach((k) => storage.removeItem(k));
  } catch {
    // noop
  }
};
